import { InputBaseComponentProps } from '@mui/material';
import { formatPhoneEntry, toE164Phone, toPhoneEntry, toPhoneEntryFromE164 } from '@/utils/phone-utils';
import { parseDigits } from 'libphonenumber-js/max';
import React, { ChangeEvent, KeyboardEvent, ReactElement, Ref, forwardRef, useCallback, useRef, useState } from 'react';

interface Props extends InputBaseComponentProps {
  name?: string;
}

/**
 * Tells a character the number is made of from one the formatter inserted.
 * Only these are counted when a caret position is carried across a reformat,
 * so the caret stays beside the same digit however the separators move.
 *
 * @param character The character to classify.
 */
const isPhoneCharacter = (character: string): boolean => character === '+' || parseDigits(character) !== '';

/**
 * Counts the phone characters lying before `offset`.
 *
 * @param text The text to walk.
 * @param offset The offset to stop at.
 */
const countPhoneCharacters = (text: string, offset: number): number => {
  let count = 0;
  const limit: number = Math.min(offset, text.length);
  for (let index = 0; index < limit; index += 1) {
    if (isPhoneCharacter(text[index])) {
      count += 1;
    }
  }
  return count;
};

/**
 * Finds the offset that follows `count` phone characters,
 * which is where the caret belongs once the text has been reformatted around it.
 *
 * @param text The formatted text.
 * @param count The number of phone characters the caret should follow.
 */
const offsetAfterPhoneCharacters = (text: string, count: number): number => {
  if (count <= 0) {
    return 0;
  }
  let seen = 0;
  for (let index = 0; index < text.length; index += 1) {
    if (isPhoneCharacter(text[index])) {
      seen += 1;
      if (seen === count) {
        return index + 1;
      }
    }
  }
  return text.length;
};

/**
 * Re-presents a change event carrying the E.164 value in place of the text on screen.
 * The element is layered with `Object.create` rather than replaced by an object literal,
 * so a consumer reading `target.type` or calling `preventDefault()` keeps working.
 *
 * @param element The input the user typed into.
 * @param value The E.164 value the consumer should see.
 * @param name The field name, for a wrapper that reports the field under a name of its own.
 */
const asChangeEvent = (element: HTMLInputElement, value: string, name?: string): ChangeEvent<HTMLInputElement> => {
  const overrides: PropertyDescriptorMap = { value: { value, enumerable: true } };
  if (name != null) {
    overrides.name = { value: name, enumerable: true };
  }
  const target: HTMLInputElement = Object.create(element, overrides);
  return {
    target,
    currentTarget: target,
    type: 'change',
    bubbles: true,
    preventDefault: (): void => undefined,
    stopPropagation: (): void => undefined,
  } as unknown as ChangeEvent<HTMLInputElement>;
};

/**
 * Unmasked phone input that formats as the user types and reports the number in E.164,
 * accepting any country. Designed to be used as `inputComponent` for MUI `OutlinedInput`;
 * the masked Russia-only variant is `RussianPhoneNumberField`.
 *
 * The characters the user entered are the component's own state, and the only state there is.
 * They cannot be derived from the `value` prop, because one E.164 number has several spellings here —
 * `+79001234567` reads as `900 123-45-67`, `8 (900) 123-45-67` or `+7 900 123 45 67`
 * depending on how it was entered — so the prop alone cannot say which the user is looking at.
 *
 * A value set from outside is therefore adopted **during render** rather than from an effect,
 * and only when the prop itself changed to something this field did not report.
 * An effect reads the render it was scheduled from, so a pair of effects reconciling a prop against internal state
 * can fire together on stale snapshots and swap the two values back and forth forever;
 * that is what made the previous implementation hang the page once a second character arrived
 * before the first one's effects had run. Reporting the change straight from the event handler
 * keeps the form's update in the same batch as this one, so the two never drift apart.
 *
 * The ref is forwarded explicitly rather than left to ride the props spread,
 * so MUI's `inputRef` reaches the input without depending on React 19 treating `ref` as an ordinary prop.
 */
export const PhoneNumberInput = forwardRef<HTMLInputElement, Props>(function PhoneNumberInput(
  { value, onChange, onKeyDown, autoComplete = 'tel', ...other }: Props,
  ref: Ref<HTMLInputElement>,
): ReactElement {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const setRef = useCallback(
    (element: HTMLInputElement | null): void => {
      inputRef.current = element;
      if (typeof ref === 'function') {
        ref(element);
      } else if (ref) {
        ref.current = element;
      }
    },
    [ref],
  );

  const incoming: string = value != null ? String(value) : '';
  const [entry, setEntry] = useState<string>((): string => toPhoneEntryFromE164(incoming));
  const [lastValue, setLastValue] = useState<string>(incoming);

  let current: string = entry;
  if (incoming !== lastValue) {
    setLastValue(incoming);
    if (incoming !== toE164Phone(entry)) {
      current = toPhoneEntryFromE164(incoming);
      setEntry(current);
    }
  }

  /**
   * Applies an edit: reformats the text, puts the caret back beside the same digit,
   * and reports the number to the form.
   *
   * @param nextEntry The phone characters after the edit.
   * @param caretAt The number of phone characters the caret should follow.
   */
  const applyEdit = (nextEntry: string, caretAt: number): void => {
    const element: HTMLInputElement | null = inputRef.current;
    const nextText: string = formatPhoneEntry(nextEntry);
    if (element) {
      // Writing the value here rather than leaving it to the render keeps the caret put:
      // React finds the input already showing what it is about to render and leaves it alone.
      element.value = nextText;
      const caret: number = offsetAfterPhoneCharacters(nextText, caretAt);
      element.setSelectionRange(caret, caret);
    }
    setEntry(nextEntry);
    if (onChange && element) {
      onChange(asChangeEvent(element, toE164Phone(nextEntry), other.name));
    }
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const element: HTMLInputElement = event.target;
    const nextEntry: string = toPhoneEntry(element.value);
    const caretAt: number = countPhoneCharacters(element.value, element.selectionStart ?? element.value.length);
    applyEdit(nextEntry, Math.min(caretAt, nextEntry.length));
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (onKeyDown) {
      onKeyDown(event);
    }
    if (event.defaultPrevented || (event.key !== 'Backspace' && event.key !== 'Delete')) {
      return;
    }
    const element: HTMLInputElement = event.currentTarget;
    // Taking the key over would otherwise rewrite a field the user is not allowed to edit,
    // because no change event follows to correct it.
    if (element.readOnly) {
      return;
    }
    const start: number = element.selectionStart ?? 0;
    // A selection is deleted by the browser as usual; the change event that follows reformats it.
    if (start !== (element.selectionEnd ?? start)) {
      return;
    }
    // Both keys are taken over so that they erase a digit rather than a separator:
    // erasing a separator would leave the number unchanged and the key looking stuck.
    event.preventDefault();
    const currentEntry: string = toPhoneEntry(element.value);
    const caretAt: number = countPhoneCharacters(element.value, start);
    const index: number = event.key === 'Backspace' ? caretAt - 1 : caretAt;
    if (index < 0 || index >= currentEntry.length) {
      return;
    }
    applyEdit(currentEntry.slice(0, index) + currentEntry.slice(index + 1), index);
  };

  return (
    <input
      {...other}
      ref={setRef}
      autoComplete={autoComplete}
      value={formatPhoneEntry(current)}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
    />
  );
});
